import ModalService from '@/modules/modals/services/modal.service';
import WCConfirmationModal from '@/components/WCConfirmationModal/WCConfirmationModal.vue';
import WCEditOrderModal from '@/modules/orders/components/WCEditOrderModal/WCEditOrderModal.vue';
import WCEditCartModal from '@/modules/orders/components/WCEditCartModal/WCEditCartModal.vue';
import { mapActions, mapGetters } from 'vuex';
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import WCAddToListToast from '@/modules/lists/components/WCAddToListToast/WCAddToListToast.vue';
import axios from 'axios';
import AlertService from '@/modules/alerts/services/AlertService';
import WCEditOrderCompleteAlert from '@/modules/orders/components/WCEditOrderCompleteAlert/WCEditOrderCompleteAlert.vue';
import WCReceiptModal from '@/modules/orders/components/WCReceiptModal/WCReceiptModal.vue';
import { MODAL_EVENTS } from '@/constants/EventConstants';
import ItemAddToListMixin from '@/modules/lists/mixins/ItemAddToListMixin';
import AnalyticsService from '@/services/analytics.service';
import TrackableEventConstants from '@/constants/TrackableEventConstants';

/**
 * Mixin to hold the order panel related functionalities.
 * It is being used by WCOrderPanel & WCMobileOrderPanel
 */
export default {
  props: {
    order: {
      required: false,
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      cartItemsCount: 'cart/getCartLineItemCount',
      lineItems: 'cart/getLineItems',
    }),
  },
  mixins: [ItemAddToListMixin],
  methods: {
    ...mapActions({
      removeAllCartItems: 'cart/removeAllCartItems',
      loadDetails: 'lists/loadDetails',
      setLoadingStatus: 'cart/setLoadingStatus',
      reloadCart: 'cart/reload',
    }),
    /**
     * Method to cancel the order
     * @param {Object} order - Order details object
     */
    async cancelOrder(order) {
      try {
        const response = await ModalService.open(WCConfirmationModal, {
          header: `${this.$t('cancelQuestion')}`,
          content: this.$t('areYouSureCancelOrder'),
          confirm: this.$t('yes'),
          cancel: this.$t('no'),
        });
        if (response === MODAL_EVENTS.CONFIRM) {
          ToastService.open(WCSimpleToast, {
            props: {
              variant: 'primary',
              title: this.$t('cancelOrder'),
              message: 'Retrieving your order, this may take a minute',
            },
            timeout: 5000,
          });
          this.setLoadingStatus(true);
          await axios.post(`/api/me/orders/${order.scancode}/cancel`);
          this.setLoadingStatus(false);
          ToastService.open(WCSimpleToast, {
            props: {
              variant: 'success',
              title: this.$t('orderCancelled'),
              message: this.$t(
                'orderCancelledSuccess',
                order.scancode ? { orderScanCode: `#${order.scancode}` } : { orderScanCode: '' },
              ),
            },
            timeout: 5000,
          });

          AnalyticsService.track(TrackableEventConstants.Refund, {
            scancode: order.scancode,
            total: order.total,
            lineItems: order.lineItems,
          });

          this.$router.push({
            name: 'Home',
          });
        }
      } catch (error) {
        console.error(error);
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: this.$t('error'),
            message: this.$t('orderCancelledError'),
          },
          timeout: 5000,
        });
      } finally {
        // Once dialog is closed move focus to the related element
        if (
          this.$refs.orderPanelHeaderRef &&
          this.$refs.orderPanelHeaderRef.$refs.modalTriggerButton
        )
          this.$refs.orderPanelHeaderRef.$refs.modalTriggerButton.focus();
      }
    },

    /**
     * Method to edit the order
     * @param {Object} order - Order details object
     */
    async editOrder(order) {
      try {
        let response = '';
        if (this.cartItemsCount > 0) {
          response = await ModalService.open(WCEditCartModal);
        } else if (this.cartItemsCount === 0) {
          response = await ModalService.open(WCEditOrderModal);
        }
        if (response === 'Order edited' || response === 'addItemsToCart') {
          this.editOrderRequest(order);
        } else if (response === 'saveItemsForLater') {
          this.saveAllLater();
          this.editOrderRequest(order);
        }
      } catch (error) {
        // NOOP
      } finally {
        // Once dialog is closed move focus to the related element
        if (
          this.$refs.orderPanelHeaderRef &&
          this.$refs.orderPanelHeaderRef.$refs.modalTriggerButton
        )
          this.$refs.orderPanelHeaderRef.$refs.modalTriggerButton.focus();
      }
    },

    /**
     * Method to send the edit order request
     * @param {Object} order - Order details object
     */
    async editOrderRequest(order) {
      this.setLoadingStatus(true);
      try {
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'primary',
            title: this.$t('editOrder'),
            message: 'Retrieving your order, this may take a minute',
          },
          timeout: 5000,
        });
        await axios.post(`/api/me/orders/${order.scancode}/edit`, {});
        this.setLoadingStatus(false);
        await this.reloadCart();
        this.$router.push({
          name: 'Home',
        });

        AlertService.show(WCEditOrderCompleteAlert, {
          props: { variant: 'success' },
          timeout: 10000,
        });

        AnalyticsService.track(TrackableEventConstants.Refund, {
          scancode: order.scancode,
          total: order.total,
          lineItems: order.lineItems,
        });
      } catch (error) {
        console.error(error);
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: this.$t('error'),
            message: this.$t('editOrderError'),
          },
          timeout: 7000,
        });
      }
    },
    /**
     * Method to add all items to Save for Later list
     */
    async saveAllLater() {
      try {
        const list = await this.addItemsToList({
          list: { name: 'Save for later', type: 'SAVE_FOR_LATER' },
          items: this.lineItems,
        });
        this.removeAllCartItems();
        this.loadDetails({ list });
        ToastService.open(WCAddToListToast, {
          props: {
            variant: 'favorite',
            title: this.$t('itemAddedToSaveForLater'),
            list,
            close: {},
            dismiss: {},
          },
          timeout: 2000,
        });
      } catch (error) {
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: this.$t('error'),
            message: error.message,
          },
          timeout: 7000,
        });
      }
    },
    async viewReceipt(order) {
      try {
        await ModalService.open(WCReceiptModal, { scancode: order.scancode });
      } catch (error) {
        console.error(error);
      }
    },
  },
};
